// src/services/withdrawalService.js
import { getValidSignature, clearSignatureCache } from './signatureService';

/**
 * Create a withdrawal request for the user's commission
 * @param {Object} wallet - Wallet information
 * @param {number} amount - Amount to withdraw
 * @param {number} nodeId - Node ID from which to withdraw commissions
 * @returns {Object} - Response from withdrawal creation API
 */
export const createWithdrawalRequest = async (wallet, amount, nodeId) => {
  if (!wallet.connected) {
    throw new Error('Wallet not connected');
  }
  
  try {
    // Get a valid signature with potential caching
    const { signature, message } = await getValidSignature(
      wallet.address,
      null, // No blockchain network ID needed
      wallet.type || 'metamask',
      false // Don't force refresh initially
    );
    
    // Build request body
    const requestBody = {
      wallet_address: wallet.address,
      signature: signature,
      message: message,
      wallet_type: wallet.type || 'metamask',
      amount: amount,
      node_id: nodeId
    };
    
    // Send request to create withdrawal
    const response = await fetch('https://api.aeronyx.network/api/commissions/withdrawal-requests/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to create withdrawal request');
    }
    
    return result.data;
  } catch (err) {
    console.error('Error creating withdrawal request:', err);
    
    // Clear signature cache if there's an auth error
    if (err.message && (
      err.message.toLowerCase().includes('signature') || 
      err.message.toLowerCase().includes('auth') ||
      err.message.toLowerCase().includes('token'))
    ) {
      clearSignatureCache();
      // Retry with fresh signature
      return createWithdrawalRequest(wallet, amount, nodeId);
    }
    
    throw err;
  }
};

/**
 * Get user's withdrawal history
 * @param {Object} wallet - Wallet information
 * @param {Object} params - Parameters for pagination and filtering
 * @returns {Object} - Response with withdrawal history
 */
export const getUserWithdrawals = async (wallet, params = {}) => {
  if (!wallet.connected) {
    throw new Error('Wallet not connected');
  }
  
  try {
    // Get a valid signature with potential caching
    const { signature, message } = await getValidSignature(
      wallet.address,
      null, // No blockchain network ID needed
      wallet.type || 'metamask',
      false // Don't force refresh initially
    );
    
    // Build request body
    const requestBody = {
      wallet_address: wallet.address,
      signature: signature,
      message: message,
      wallet_type: wallet.type || 'metamask',
      ...params // Include pagination and filtering params
    };
    
    // Send request to get withdrawal history
    const response = await fetch('https://api.aeronyx.network/api/commissions/withdrawal-history/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch withdrawal history');
    }
    
    return result.data;
  } catch (err) {
    console.error('Error fetching withdrawal history:', err);
    
    // Clear signature cache if there's an auth error
    if (err.message && (
      err.message.toLowerCase().includes('signature') || 
      err.message.toLowerCase().includes('auth') ||
      err.message.toLowerCase().includes('token'))
    ) {
      clearSignatureCache();
      // Try again with fresh signature
      return getUserWithdrawals(wallet, params);
    }
    
    throw err;
  }
};

/**
 * Get withdrawal details for a specific withdrawal ID
 * @param {Object} wallet - Wallet information
 * @param {string} withdrawalId - Withdrawal ID to fetch details for
 * @returns {Object} - Response with withdrawal details
 */
export const getWithdrawalDetails = async (wallet, withdrawalId) => {
  if (!wallet.connected) {
    throw new Error('Wallet not connected');
  }
  
  try {
    // Get a valid signature with potential caching
    const { signature, message } = await getValidSignature(
      wallet.address,
      null, // No blockchain network ID needed
      wallet.type || 'metamask',
      false // Don't force refresh initially
    );
    
    // Build request body
    const requestBody = {
      wallet_address: wallet.address,
      signature: signature,
      message: message,
      wallet_type: wallet.type || 'metamask',
      withdrawal_id: withdrawalId
    };
    
    // Send request to get withdrawal details
    const response = await fetch('https://api.aeronyx.network/api/commissions/withdrawal-details/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch withdrawal details');
    }
    
    return result.data;
  } catch (err) {
    console.error('Error fetching withdrawal details:', err);
    
    // Clear signature cache if there's an auth error
    if (err.message && (
      err.message.toLowerCase().includes('signature') || 
      err.message.toLowerCase().includes('auth') ||
      err.message.toLowerCase().includes('token'))
    ) {
      clearSignatureCache();
      // Try again with fresh signature
      return getWithdrawalDetails(wallet, withdrawalId);
    }
    
    throw err;
  }
};

/**
 * Check if commissions can be withdrawn and their withdrawal status
 * @param {Object} wallet - Wallet information
 * @param {number} nodeId - Node ID to check commissions for
 * @returns {Object} - Response with commission withdrawal status
 */
export const getCommissionWithdrawalStatus = async (wallet, nodeId) => {
  if (!wallet.connected) {
    throw new Error('Wallet not connected');
  }
  
  try {
    // Get a valid signature with potential caching
    const { signature, message } = await getValidSignature(
      wallet.address,
      null, // No blockchain network ID needed
      wallet.type || 'metamask',
      false // Don't force refresh initially
    );
    
    // Build request body
    const requestBody = {
      wallet_address: wallet.address,
      signature: signature,
      message: message,
      wallet_type: wallet.type || 'metamask',
      node_id: nodeId
    };
    
    // Send request to check withdrawal status
    const response = await fetch('https://api.aeronyx.network/api/commissions/withdrawal-status/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to check withdrawal status');
    }
    
    return result.data;
  } catch (err) {
    console.error('Error checking withdrawal status:', err);
    
    // Clear signature cache if there's an auth error
    if (err.message && (
      err.message.toLowerCase().includes('signature') || 
      err.message.toLowerCase().includes('auth') ||
      err.message.toLowerCase().includes('token'))
    ) {
      clearSignatureCache();
      // Try again with fresh signature
      return getCommissionWithdrawalStatus(wallet, nodeId);
    }
    
    throw err;
  }
};
